class ContactForm extends HTMLElement {
  constructor() {
    super();
    this.nameRegex = /^[A-Za-z\s\-']+$/;
    this.emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Proper binding for event listeners
    this.boundDropdownPosition = this.handleUpdateDropdownPosition.bind(this);
    this.boundHandleAllowSubmit = this.handleAllowSubmit.bind(this);
    this.boundHandleTextArea = this.handleTextArea.bind(this);
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
    this.boundHandleClearTextArea = this.handleClearTextArea.bind(this);
  }

  connectedCallback() {
    this.init();
    this.phoneToken = "";
    this.iti = null;
    this.submitButton.disabled = true;
    this.textareaMaxChars = 100;
  }

  init() {
    // DOM elements
    this.domElements();

    // Bind listeners
    this.boundListeners();

    // Init phoneInput
    document.addEventListener(
      "DOMContentLoaded",
      this.handleInitPhoneInput.bind(this)
    );
    this.handleToggleClearIcon();

    // Handle success redirect
    this.handleSuccess();
  }

  domElements() {
    this.form = this.querySelector("form#ContactForm");
    this.submitButton = this.querySelector('button[type="submit"]');
    this.contactForm = this.querySelector(".contact-form");
    this.successPage = this.querySelector(".success-page");
    this.textarea = this.querySelector("#ContactForm-body");
    this.phoneInput = this.querySelector("#ContactForm-phone");
    this.clearIcon = this.querySelector("#clear-icon");
    this.countSpanEl = this.querySelector(".text-area-count");
    this.requiredFieldIds = [
      "ContactForm-first-name",
      "ContactForm-last-name",
      "ContactForm-email",
      "ContactForm-phone",
      "ContactForm-body",
    ];
  }

  boundListeners() {
    // Phone input & dropdown positioning listeners
    this.phoneInput.addEventListener(
      "open:countrydropdown",
      this.boundDropdownPosition
    );
    window.addEventListener("resize", this.boundDropdownPosition);
    document.addEventListener("scroll", this.boundDropdownPosition, true);

    // Form field validation and textarea counting
    this.form.addEventListener("input", () => {
      this.boundHandleAllowSubmit();
      this.boundHandleTextArea();
    });
    this.form.addEventListener("blur", () => {
      this.boundHandleAllowSubmit();
      this.boundHandleTextArea();
    });

    // Submit validation
    this.form.addEventListener("submit", this.boundHandleFormSubmit);

    // Handle clear textarea click
    if (!this.clickHandler) {
      this.clickHandler = (e) => {
        if (e.target.closest("#clear-icon")) this.boundHandleClearTextArea();
      };
    }
    this.addEventListener("click", this.clickHandler);
  }

  handleInitPhoneInput() {
    if (!this.phoneInput) {
      console.error("Phone input not found");
      return;
    }
    this.iti = window.intlTelInput(this.phoneInput, {
      initialCountry: "auto",
      separateDialCode: true,
      nationalMode: false,
      dropdownContainer: document.body,
      autoPlaceholder: "polite",
      placeholderNumberType: "MOBILE",
      geoIpLookup: (callback) => {
        fetch(`https://ipinfo.io/json?token=${this.phoneToken}`)
          .then((resp) => resp.json())
          .then((resp) => callback(resp.country))
          .catch(() => callback("us"));
      },
      // utilsScript: "{{ 'utils.js' | asset_url }}",
    });
  }

  handleUpdateDropdownPosition() {
    const countryListContainer = document.querySelector(".iti.iti--container");
    if (!countryListContainer || !this.phoneInput) return;
    let rect = this.phoneInput.getBoundingClientRect();
    const wrapperEl = this.phoneInput.closest(
      ".input-wrapper.input-wrapper--phone"
    );
    if (wrapperEl) rect = wrapperEl.getBoundingClientRect();
    countryListContainer.style.position = "absolute";
    countryListContainer.style.top = `${rect.bottom + window.scrollY}px`;
    countryListContainer.style.left = `${rect.left + window.scrollX + 1}px`;
    countryListContainer.style.width = `${rect.width}px`;
  }

  showError(field, message) {
    const errorEl = this.querySelector(`#${field.id}-error`);
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = "block";
    }
    field.classList.add("field--error");
  }

  hideError(field) {
    const errorEl = this.querySelector(`#${field.id}-error`);
    if (errorEl) {
      errorEl.textContent = "";
      errorEl.style.display = "none";
    }
    field.classList.remove("field--error");
  }

  getIconSpan(id) {
    return this.querySelector(`#${id}-status-icon`);
  }

  validateField(field) {
    const value = field.value.trim();
    const id = field.id;
    const iconSpan = this.getIconSpan(id);
    const icon = iconSpan ? iconSpan.querySelector("i") : null;

    if (!value) {
      this.showError(field, "This field is required.");
      if (iconSpan) {
        icon.className = "fa-solid fa-triangle-exclamation";
        icon.style.color = "red";
        iconSpan.style.display = "inline";
      }
      return false;
    }

    const isNameField = [
      "ContactForm-first-name",
      "ContactForm-last-name",
    ].includes(id);
    if (isNameField && !this.nameRegex.test(value)) {
      this.showError(
        field,
        "Only letters, spaces, hyphens, and apostrophes are allowed."
      );
      return false;
    }

    if (id === "ContactForm-email") {
      if (!this.emailRegex.test(value)) {
        this.showError(field, "Please enter a valid email.");
        if (iconSpan) {
          icon.className = "fa-solid fa-triangle-exclamation";
          icon.style.color = "red";
          iconSpan.style.display = "inline";
        }
        return false;
      } else {
        if (iconSpan) {
          icon.className = "fa-solid fa-check";
          icon.style.color = "green";
          iconSpan.style.display = "inline";
        }
      }
    }

    if (id === "ContactForm-phone") {
      const isValidNumber = this.iti.isValidNumber();
      if (isValidNumber) {
        field.value = this.iti.getNumber();
        if (iconSpan) {
          icon.className = "fa-solid fa-check";
          icon.style.color = "green";
          iconSpan.style.display = "inline";
        }
      } else {
        this.showError(field, "Please enter a valid phone number.");
        if (iconSpan) {
          icon.className = "fa-solid fa-triangle-exclamation";
          icon.style.color = "red";
          iconSpan.style.display = "inline";
        }
        return false;
      }
    }

    this.hideError(field);
    if (id !== "ContactForm-email" && id !== "ContactForm-phone" && iconSpan) {
      iconSpan.style.display = "none";
    }
    return true;
  }

  handleAllowSubmit() {
    // Only check for presence
    const allPresent = this.requiredFieldIds.some((id) => {
      const field = this.querySelector(`#${id}`);
      return field && field.value.trim();
    });
    this.submitButton.disabled = !allPresent;
  }

  handleTextArea() {
    if (!this.textarea) {
      console.error("Textarea element not found");
      return;
    }
    if (!this.countSpanEl) {
      console.error("Count span element not found");
      return;
    }
    const currentCharsLength = this.textarea.value.length;
    this.countSpanEl.textContent = currentCharsLength.toString();

    // Limit input to maxChars
    if (currentCharsLength === this.textareaMaxChars) {
      this.textarea.value = this.textarea.value.substring(
        0,
        this.textareaMaxChars
      );
      this.showError(
        this.textarea,
        `Maximum ${this.textareaMaxChars} characters allowed.`
      );
    } else {
      this.hideError(this.textarea);
    }
    this.handleToggleClearIcon();
  }

  handleToggleClearIcon() {
    if (this.textarea && this.clearIcon)
      this.clearIcon.style.display = this.textarea.value.trim()
        ? "flex"
        : "none";
  }

  handleClearTextArea() {
    if (!this.textarea || !this.clearIcon || !this.countSpanEl) return;
    this.textarea.value = "";
    this.clearIcon.style.display = "none";
    this.countSpanEl.textContent = "0";
    this.textarea.focus();
  }

  handleSuccess() {
    const urlParams = new URLSearchParams(window.location.search);
    const isSuccess = urlParams.get("contact_posted") === "true";
    if (isSuccess) {
      window.location.href = `${window.location.origin}/pages/thank-you`;
    }
  }

  handleFormSubmit(e) {
    e.preventDefault(); // prevent form submission by default
    e.stopImmediatePropagation()

    const allValid = this.requiredFieldIds.every((id) => {
      const field = document.querySelector(`#${id}`);
      return this.validateField(field);
    });

    if (allValid) {
      this.form.submit();
    };
  }

  disconnectedCallback() {
    // remove event listeners
    this.phoneInput.removeEventListener(
      "open:countrydropdown",
      this.boundDropdownPosition
    );
    window.removeEventListener("resize", this.boundDropdownPosition);
    document.removeEventListener("scroll", this.boundDropdownPosition, true);

    this.form.removeEventListener("input", this.boundHandleAllowSubmit);
    this.form.removeEventListener("input", this.boundHandleTextArea);
    this.form.removeEventListener("blur", this.boundHandleAllowSubmit);
    this.form.removeEventListener("blur", this.boundHandleTextArea);
    this.form.removeEventListener("submit", this.boundHandleFormSubmit);
    this.removeEventListener("click", this.clickHandler);
  }
}

customElements.define("contact-form", ContactForm);
